import React, { useCallback, useEffect, useState, Fragment } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronRightIcon, HomeIcon, LoaderIcon, AlertCircleIcon } from 'lucide-react';
import { degreeCategories, degreeInterestOptions, focusOptions, getSpecializationsForFocus } from '../data/degrees';
import { searchSchools, MatchedSchool } from '../services/matchingApi';
import { MatchedSchoolCard, DidYouKnowBanner } from '../components/MatchedSchoolCard';
import { TrustSignals } from '../components/TrustSignals';
import { useFormState } from '../hooks/useFormState';
import { ProgramContentSection } from '../components/ProgramContentSection';
import { usePageMeta } from '../hooks/usePageMeta';
export function DegreeListingPage() {
  const {
    category
  } = useParams<{
    category: string;
  }>();
  const navigate = useNavigate();
  const categoryData = degreeCategories.find(c => c.id === category);
  usePageMeta(`Online ${categoryData?.name || 'Degree'} Programs | OnlineCollegeGuide`, `Browse accredited online ${categoryData?.name?.toLowerCase() || ''} degree programs. Compare schools, explore specializations, and find the right program for your career goals.`, {
    ogImage: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const {
    formData,
    errors,
    updateFormData,
    validateHeroForm
  } = useFormState({
    areaOfStudy: categoryData?.focusValue || ''
  });
  const filteredSpecializations = formData.areaOfStudy ? getSpecializationsForFocus(formData.areaOfStudy) : [];
  // API state
  const [matchedSchools, setMatchedSchools] = useState<MatchedSchool[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const handleAreaOfStudyChange = (value: string) => {
    updateFormData('areaOfStudy', value);
    updateFormData('specialization', '');
  };
  const generalSpec = categoryData?.focusValue ? getSpecializationsForFocus(categoryData.focusValue).find(s => s.type.toLowerCase().startsWith('general') || s.type.toLowerCase().startsWith('all')) : undefined;
  // Fetch results from API
  const fetchResults = useCallback(async (degreeInterest: string, areaStudy: string, mProgram: string) => {
    setIsLoading(true);
    setApiError(null);
    setHasSearched(true);
    const result = await searchSchools({
      degreeInterest: degreeInterest || undefined,
      areaStudy: areaStudy || undefined,
      mProgram: mProgram || undefined
    });
    if (result.success) {
      setMatchedSchools(result.schools);
    } else {
      setApiError('Unable to load school matches. Please try again.');
      setMatchedSchools([]);
    }
    setIsLoading(false);
  }, []);
  // Auto-fetch on page load with the category's area of study
  useEffect(() => {
    if (categoryData?.focusValue) {
      fetchResults('', categoryData.focusValue, generalSpec?.value || '');
    }
  }, [categoryData?.focusValue, fetchResults, generalSpec?.value]);
  const handleSidebarSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateHeroForm()) {
      fetchResults(formData.degreeInterest, formData.areaOfStudy, formData.specialization);
    }
  };
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [category]);
  if (!categoryData) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center bg-light px-4" data-id="element-784">
        <h1 className="text-3xl font-bold text-navy mb-4" data-id="element-785">
          Category Not Found
        </h1>
        <p className="text-gray-600 mb-8" data-id="element-786">
          We couldn't find the degree category you're looking for.
        </p>
        <Link to="/" className="text-white bg-navy px-6 py-2 rounded-md hover:bg-navy-light transition-colors" data-id="element-787">
          Return Home
        </Link>
      </div>;
  }
  return <main className="flex-grow bg-light pb-16" data-id="element-788">
      {/* Page Header */}
      <div className="bg-navy text-white py-12" data-id="element-789">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-790">
          {/* Breadcrumbs */}
          <nav className="flex text-sm text-gray-300 mb-6" aria-label="Breadcrumb" data-id="element-791">
            <ol className="inline-flex items-center space-x-1 md:space-x-3" data-id="element-792">
              <li className="inline-flex items-center" data-id="element-793">
                <Link to="/" className="inline-flex items-center hover:text-white transition-colors" data-id="element-794">
                  <HomeIcon className="w-4 h-4 mr-2" data-id="element-795" />
                  Home
                </Link>
              </li>
              <li data-id="element-796">
                <div className="flex items-center" data-id="element-797">
                  <ChevronRightIcon className="w-4 h-4 mx-1" data-id="element-798" />
                  <span className="ml-1 md:ml-2" data-id="element-799">Degrees</span>
                </div>
              </li>
              <li aria-current="page" data-id="element-800">
                <div className="flex items-center" data-id="element-801">
                  <ChevronRightIcon className="w-4 h-4 mx-1" data-id="element-802" />
                  <span className="ml-1 md:ml-2 text-white font-medium" data-id="element-803">
                    {categoryData.name}
                  </span>
                </div>
              </li>
            </ol>
          </nav>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-white" data-id="element-804">
            Online {categoryData.name} Degrees
          </h1>
          <p className="text-lg text-gray-300 max-w-3xl" data-id="element-805">
            {categoryData.description} Compare top accredited programs and find
            the right fit for your career goals.
          </p>
        </div>
      </div>

      <TrustSignals data-id="element-806" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-807">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-808">
          {/* Main Content - Listings */}
          <div className="lg:w-3/4 order-2 lg:order-1" data-id="element-809">
            {/* Loading State */}
            {isLoading && <div className="flex flex-col items-center justify-center py-16" data-id="element-810">
                <LoaderIcon className="w-8 h-8 text-navy animate-spin mb-4" data-id="element-811" />
                <p className="text-gray-600 font-medium" data-id="element-812">
                  Finding your best school matches...
                </p>
              </div>}

            {/* Error State */}
            {!isLoading && apiError && <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-3" data-id="element-813">
                <AlertCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-814" />
                <div data-id="element-815">
                  <p className="text-red-800 font-medium" data-id="element-816">{apiError}</p>
                  <p className="text-red-600 text-sm mt-1" data-id="element-817">
                    Try adjusting your search criteria or check back later.
                  </p>
                </div>
              </div>}

            {/* Results */}
            {!isLoading && !apiError && matchedSchools.length > 0 && <div className="space-y-4" data-id="element-818">
                {matchedSchools.map((school, idx) => <Fragment key={(school as Record<string, unknown>).schoolid as string || idx} data-id="element-819">
                    <MatchedSchoolCard school={school} index={idx} data-id="element-820" />
                    {idx === 0 && matchedSchools.length > 1 && <DidYouKnowBanner data-id="element-821" />}
                  </Fragment>)}
              </div>}

            {/* Empty State */}
            {!isLoading && !apiError && hasSearched && matchedSchools.length === 0 && <div className="bg-white rounded-xl border border-gray-200 p-12 text-center" data-id="element-822">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4" data-id="element-823">
                    <AlertCircleIcon className="w-8 h-8 text-gray-400" data-id="element-824" />
                  </div>
                  <h3 className="text-xl font-bold text-navy mb-2" data-id="element-825">
                    No matches found
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto" data-id="element-826">
                    Try adjusting your degree level or program selection in the
                    sidebar to find more results.
                  </p>
                </div>}
          </div>

          {/* Sidebar - Form */}
          <div className="lg:w-1/4 order-1 lg:order-2" data-id="element-827">
            <div className="sticky top-24" data-id="element-828">
              <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden mb-6" data-id="element-829">
                <div className="bg-[#1B365D] px-6 py-4" data-id="element-830">
                  <h3 className="text-white font-bold text-center text-sm tracking-widest uppercase" data-id="element-831">
                    Get Matched Fast
                  </h3>
                  <p className="text-gray-300 text-xs text-center mt-1" data-id="element-832">
                    Find the right program for you
                  </p>
                </div>

                <form onSubmit={handleSidebarSubmit} className="p-5 space-y-4" data-id="element-833">
                  {/* Degree Level */}
                  <div data-id="element-834">
                    <select id="sidebar-degreeInterest" value={formData.degreeInterest} onChange={e => updateFormData('degreeInterest', e.target.value)} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.degreeInterest ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-835">
                      <option value="" data-id="element-836">Select Degree Level</option>
                      {degreeInterestOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-837">
                          {opt.type}
                        </option>)}
                    </select>
                    {errors.degreeInterest && <p className="mt-1 text-xs text-red-600" data-id="element-838">
                        {errors.degreeInterest}
                      </p>}
                  </div>

                  {/* Area of Study */}
                  <div data-id="element-839">
                    <select id="sidebar-areaOfStudy" value={formData.areaOfStudy} onChange={e => handleAreaOfStudyChange(e.target.value)} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.areaOfStudy ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-840">
                      <option value="" data-id="element-841">Select Area of Study</option>
                      {focusOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-842">
                          {opt.type}
                        </option>)}
                    </select>
                    {errors.areaOfStudy && <p className="mt-1 text-xs text-red-600" data-id="element-843">
                        {errors.areaOfStudy}
                      </p>}
                  </div>

                  {/* Specialization / Program */}
                  <div data-id="element-844">
                    <select id="sidebar-specialization" value={formData.specialization} onChange={e => updateFormData('specialization', e.target.value)} disabled={!formData.areaOfStudy} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed ${errors.specialization ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-845">
                      <option value="" data-id="element-846">
                        {formData.areaOfStudy ? 'Select Program' : 'Select an area of study first'}
                      </option>
                      {filteredSpecializations.map(opt => <option key={opt.value} value={opt.value} data-id="element-847">
                          {opt.type}
                        </option>)}
                    </select>
                    {errors.specialization && <p className="mt-1 text-xs text-red-600" data-id="element-848">
                        {errors.specialization}
                      </p>}
                  </div>

                  <button type="submit" disabled={isLoading} className="w-full bg-[#D4A943] hover:bg-[#B38B2E] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-4 px-4 rounded-lg text-sm tracking-wider uppercase transition-colors duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2" data-id="element-849">
                    {isLoading && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-850" />}
                    {isLoading ? 'Searching...' : 'Search Programs'}
                  </button>
                </form>
              </div>

              {/* Related Programs */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-851">
                <h4 className="font-bold text-navy mb-4 border-b border-gray-100 pb-2" data-id="element-852">
                  Programs in {categoryData.name}
                </h4>
                <ul className="space-y-2" data-id="element-853">
                  {getSpecializationsForFocus(formData.areaOfStudy || categoryData.focusValue).map(spec => <li key={spec.value} data-id="element-854">
                      <button onClick={() => {
                    updateFormData('specialization', spec.value);
                    fetchResults(formData.degreeInterest, formData.areaOfStudy || categoryData.focusValue, spec.value);
                  }} className={`text-sm flex items-center w-full text-left ${formData.specialization === spec.value ? 'text-crimson font-semibold' : 'text-gray-600 hover:text-crimson'}`} data-id="element-855">
                        <ChevronRightIcon className="w-3 h-3 mr-1 flex-shrink-0" data-id="element-856" />
                        {spec.type}
                      </button>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ProgramContentSection degreeInterest={formData.degreeInterest} areaOfStudy={formData.areaOfStudy || categoryData.focusValue} specialization={formData.specialization || generalSpec?.value || ''} data-id="element-857" />
    </main>;
}